import { createAuthClient } from "better-auth/react";
import { twoFactorClient } from "better-auth/client/plugins";
import { passkeyClient } from "@better-auth/passkey/client";
import { ssoClient } from "@better-auth/sso/client";
import { ENVS } from "config/envs";
import { BETTER_AUTH_BASE_PATH } from "config/better-auth.constants";
import { resolveBetterAuthBearerToken, setBearerSessionToken } from "lib/better-auth-session";
import { notifyBetterAuthTwoFactorRedirect } from "lib/better-auth-two-factor-bridge";
import {
  captureAuthSession,
  isAuthSessionUnchanged,
  type AuthSessionOwner,
} from "lib/auth-session-ownership";

const requestOwners = new WeakMap<object, AuthSessionOwner>();

export const authClient = createAuthClient({
  baseURL: ENVS.backendUrl,
  basePath: BETTER_AUTH_BASE_PATH,
  plugins: [
    ssoClient(),
    passkeyClient(),
    twoFactorClient({
      onTwoFactorRedirect: () => {
        notifyBetterAuthTwoFactorRedirect();
      },
    }),
  ],
  fetchOptions: {
    credentials: "include",
    onRequest: (request) => {
      const bearerToken = resolveBetterAuthBearerToken();
      const expectedHeader = bearerToken ? `Bearer ${bearerToken}` : null;
      if (request.headers.get("authorization") === expectedHeader) {
        requestOwners.set(request, captureAuthSession());
      }
    },
    onSuccess: (ctx) => {
      const authToken = ctx.response.headers.get("set-auth-token");
      const owner = requestOwners.get(ctx.request);
      if (authToken && owner && isAuthSessionUnchanged(owner)) {
        setBearerSessionToken(authToken);
      }
    },
    auth: {
      type: "Bearer",
      token: () => resolveBetterAuthBearerToken(),
    },
  },
});

export const {
  signIn,
  signUp,
  signOut,
  getSession,
  useSession,
  listSessions,
  revokeSession,
  revokeOtherSessions,
  revokeSessions,
  requestPasswordReset,
  resetPassword,
  sendVerificationEmail,
} = authClient;
